"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { featureFlags } from "@/config/features";
import { authClient } from "@/lib/auth-client";

// Intercom types (copied from lazy-intercom.tsx for consistency)
declare global {
  interface Window {
    intercomSettings?: {
      app_id: string;
      api_base: string;
      user_id?: string;
      email?: string;
      name?: string;
    };
    Intercom?: IntercomFunc;
    attachEvent?: (event: string, handler: () => void) => void;
  }
}

interface IntercomFunc {
  (action: string, ...args: unknown[]): void;
  q?: unknown[];
  c?: (args: unknown[]) => void;
}

interface IntercomUser {
  id: string;
  email: string;
  name?: string | null;
}

const INTERCOM_APP_ID = "gm1k4jta";
const INTERCOM_API_BASE = "https://api-iam.intercom.io";
const LOAD_DELAY_MS = 5000;

/**
 * Debounce delay before acting on session loss.
 * Prevents transient auth flickers from triggering unnecessary Intercom cleanup.
 * Brief losses (< 3s) are ignored; sustained losses (> 3s) trigger shutdown.
 */
const SESSION_LOSS_DEBOUNCE_MS = 3000;

type IntercomState =
  | { type: "idle" }
  | { type: "loading" }
  | { type: "loaded"; user: IntercomUser | null }
  | { type: "shutdown" };

/**
 * Check if we should update Intercom with new user identity
 */
function shouldUpdateUser(
  isLoaded: boolean,
  currentUser: IntercomUser | null,
  sessionUser: IntercomUser | null,
): sessionUser is IntercomUser {
  return isLoaded && currentUser === null && sessionUser !== null;
}

/**
 * Check if user identity has changed
 */
function hasUserChanged(
  isLoaded: boolean,
  currentUser: IntercomUser | null,
  sessionUser: IntercomUser | null,
): sessionUser is IntercomUser {
  return (
    isLoaded && currentUser !== null && sessionUser !== null && currentUser.id !== sessionUser.id
  );
}

/**
 * Check if we should trigger shutdown debounce
 */
function shouldStartShutdownDebounce(
  isLoaded: boolean,
  currentUser: IntercomUser | null,
  sessionUser: IntercomUser | null,
): boolean {
  return isLoaded && currentUser !== null && sessionUser === null;
}

/**
 * Check if we should re-boot after shutdown
 */
function shouldRebootAfterShutdown(
  intercomStateType: IntercomState["type"],
  sessionUser: IntercomUser | null,
): sessionUser is IntercomUser {
  return intercomStateType === "shutdown" && sessionUser !== null;
}

/**
 * Global Intercom component with guest-first loading and auth-aware identity lifecycle.
 *
 * Features:
 * - Loads Intercom immediately (guest mode) without waiting for auth
 * - When session becomes authenticated: calls Intercom('update') with user id/email/name
 * - When session is lost: calls Intercom('shutdown') to clean up after a 3s debounce
 * - Supports re-boot for new sessions without page reload
 * - Maintains single launcher throughout all auth transitions
 *
 * @fileoverview Authenticated Intercom lifecycle manager for guest and logged-in users
 */
export function IntercomAuthLifecycle(): null {
  // Build-time disable gate. When `featureFlags.enableIntercom` is false we
  // short-circuit before any hooks register state, listeners, timers, or
  // touch `window.intercomSettings` / `window.Intercom`. This is safe under
  // React's rules-of-hooks because the gate is a stable build-time constant
  // for any given bundle (preview/production ship with the flag forced off),
  // so the hook order never varies across renders of the same build.
  if (!featureFlags.enableIntercom) {
    return null;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data: session, isPending } = authClient.useSession();
  const [intercomState, setIntercomState] = useState<IntercomState>({ type: "idle" });
  const hasLoadedRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cleanupFnsRef = useRef<(() => void)[]>([]);
  const sessionLossTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wasAuthenticatedRef = useRef(false);

  // Extract user info from session - memoized to prevent unnecessary re-renders
  const sessionUser: IntercomUser | null = useMemo(() => {
    return session?.user
      ? {
          id: session.user.id,
          email: session.user.email,
          name: session.user.name,
        }
      : null;
  }, [session]);

  /**
   * Boot Intercom with optional user identity
   * Reads current session user from the session hook to avoid stale closure issues
   */
  const bootIntercom = (user: IntercomUser | null) => {
    if (hasLoadedRef.current || typeof window === "undefined") {
      return;
    }
    hasLoadedRef.current = true;
    setIntercomState({ type: "loading" });

    // Configure Intercom settings
    window.intercomSettings = {
      app_id: INTERCOM_APP_ID,
      api_base: INTERCOM_API_BASE,
      ...(user && {
        user_id: user.id,
        email: user.email,
        ...(user.name && { name: user.name }),
      }),
    };

    // Initialize or reattach Intercom
    const w = window;
    const ic = w.Intercom;

    if (typeof ic === "function" && !ic.q) {
      // Intercom already exists - reattach and update
      ic("reattach_activator");
      ic("update", w.intercomSettings);
    } else {
      // First time boot - load the script
      const d = document;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const i: any = function (...args: unknown[]) {
        i.c(args);
      };
      i.q = [] as unknown[];
      i.c = function (args: unknown[]) {
        i.q.push(args);
      };
      w.Intercom = i as IntercomFunc;

      const loadScript = () => {
        const s = d.createElement("script");
        s.type = "text/javascript";
        s.async = true;
        s.src = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`;
        const x = d.getElementsByTagName("script")[0];
        if (x?.parentNode) {
          x.parentNode.insertBefore(s, x);
        }
      };

      if (document.readyState === "complete") {
        loadScript();
      } else if (w.attachEvent) {
        w.attachEvent("onload", loadScript);
      } else {
        w.addEventListener("load", loadScript, false);
      }
    }

    setIntercomState({ type: "loaded", user });
  };

  /**
   * Update Intercom with new user identity
   */
  const updateIntercomUser = (user: IntercomUser) => {
    if (typeof window === "undefined" || !window.Intercom) {
      return;
    }

    window.intercomSettings = {
      ...window.intercomSettings,
      app_id: INTERCOM_APP_ID,
      api_base: INTERCOM_API_BASE,
      user_id: user.id,
      email: user.email,
      ...(user.name && { name: user.name }),
    };

    window.Intercom("update", window.intercomSettings);
    setIntercomState({ type: "loaded", user });
  };

  /**
   * Shutdown Intercom and clear user identity
   */
  const shutdownIntercom = () => {
    if (typeof window === "undefined" || !window.Intercom) {
      return;
    }

    window.Intercom("shutdown");
    hasLoadedRef.current = false;
    setIntercomState({ type: "shutdown" });
  };

  /**
   * Cleanup event listeners and timers
   */
  const cleanupListeners = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    cleanupFnsRef.current.forEach((fn) => fn());
    cleanupFnsRef.current = [];
  };

  // Effect 1: Handle lazy loading triggers (delay + user activity)
  // Guest-first: boot immediately without waiting for auth state to resolve
  // FIX: Only depend on intercomState.type to prevent re-execution when sessionUser
  // reference changes. We read the current user from the stable session object.
  useEffect(() => {
    // Only boot if we're in idle or shutdown state (ready to boot)
    if (intercomState.type !== "idle" && intercomState.type !== "shutdown") {
      return;
    }

    // Skip if already loaded (shouldn't happen due to state check, but safety)
    if (hasLoadedRef.current) {
      return;
    }

    // Derive current user from the stable session object (not memoized sessionUser)
    // This ensures we have fresh data without causing effect re-runs
    const currentUser: IntercomUser | null = session?.user
      ? {
          id: session.user.id,
          email: session.user.email,
          name: session.user.name,
        }
      : null;

    const loadIntercom = () => {
      cleanupListeners();
      bootIntercom(currentUser);
    };

    const handleUserActivity = () => {
      loadIntercom();
    };

    // Set up delayed loading (5s timeout)
    timeoutRef.current = setTimeout(() => {
      loadIntercom();
    }, LOAD_DELAY_MS);

    // Also load on first user interaction (scroll or mousemove)
    const scrollListener = () => handleUserActivity();
    const mousemoveListener = () => handleUserActivity();

    window.addEventListener("scroll", scrollListener, {
      capture: true,
      passive: true,
      once: true,
    });
    window.addEventListener("mousemove", mousemoveListener, {
      capture: true,
      passive: true,
      once: true,
    });

    cleanupFnsRef.current.push(() => {
      window.removeEventListener("scroll", scrollListener, { capture: true });
      window.removeEventListener("mousemove", mousemoveListener, { capture: true });
    });

    return cleanupListeners;
    // Note: session is intentionally NOT in deps - we read it at effect run time
    // to avoid stale closures. The auth transition effect handles session changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intercomState.type]);

  /**
   * Handle auth state transitions with debounce support
   */
  const handleAuthTransition = useCallback(() => {
    // Track authenticated state for debounce logic
    if (sessionUser) {
      wasAuthenticatedRef.current = true;
    }

    const isLoaded = intercomState.type === "loaded";
    const currentUser = intercomState.type === "loaded" ? intercomState.user : null;

    // Transition: Anonymous to Authenticated (update with user identity)
    if (shouldUpdateUser(isLoaded, currentUser, sessionUser)) {
      updateIntercomUser(sessionUser);
      return;
    }

    // Transition: Authenticated to Anonymous (shutdown with debounce)
    if (shouldStartShutdownDebounce(isLoaded, currentUser, sessionUser)) {
      // Only start shutdown timer if not already debouncing
      if (!sessionLossTimerRef.current) {
        sessionLossTimerRef.current = setTimeout(() => {
          sessionLossTimerRef.current = null;
          shutdownIntercom();
        }, SESSION_LOSS_DEBOUNCE_MS);
      }
      return;
    }

    // Transition: User identity changed (update with new user)
    if (hasUserChanged(isLoaded, currentUser, sessionUser)) {
      updateIntercomUser(sessionUser);
      return;
    }

    // Transition: Re-boot after shutdown when new session appears
    if (shouldRebootAfterShutdown(intercomState.type, sessionUser)) {
      bootIntercom(sessionUser);
    }
  }, [intercomState, sessionUser]);

  // Effect 2: Handle auth state transitions
  useEffect(() => {
    if (isPending) {
      return;
    }

    handleAuthTransition();
  }, [isPending, handleAuthTransition]);

  // Effect 3: Clear debounce timer if session recovers
  useEffect(() => {
    if (isPending) {
      return;
    }

    // If session recovers while debouncing, clear the timer
    if (sessionUser && sessionLossTimerRef.current) {
      clearTimeout(sessionLossTimerRef.current);
      sessionLossTimerRef.current = null;
    }
  }, [isPending, sessionUser]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupListeners();
      if (sessionLossTimerRef.current) {
        clearTimeout(sessionLossTimerRef.current);
      }
    };
  }, []);

  return null;
}
